'use client'

import { useState } from 'react'
import Link from 'next/link'
import Footer from '@/components/Footer'
import SystemDiagram from '@/components/SystemDiagram'
import SystemSidebar from '@/components/SystemSidebar'
import { systemData, SystemType } from '@/lib/systemData'

export default function Systems() {
  const [selectedSystem, setSelectedSystem] = useState<SystemType>('content')
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null)
  const [failureScenarioIndex, setFailureScenarioIndex] = useState<number | null>(null)

  const currentSystem = systemData[selectedSystem]
  const currentComponent = selectedComponent
    ? currentSystem.components.find(c => c.id === selectedComponent) || null
    : null

  const currentFailureScenario = failureScenarioIndex !== null
    ? currentSystem.failureScenarios[failureScenarioIndex]
    : null

  const handleComponentClick = (componentId: string) => {
    setSelectedComponent(componentId)
  }

  const handleSystemChange = (system: SystemType) => {
    setSelectedSystem(system)
    setSelectedComponent(null)
    setFailureScenarioIndex(null)
  }

  const cycleFailureScenario = () => {
    if (failureScenarioIndex === null) {
      setFailureScenarioIndex(0)
    } else {
      const nextIndex = (failureScenarioIndex + 1) % currentSystem.failureScenarios.length
      if (nextIndex === 0) {
        // After cycling through all, turn off
        setFailureScenarioIndex(null)
      } else {
        setFailureScenarioIndex(nextIndex)
      }
    }
  }

  return (
    <div className="min-h-screen bg-bg-primary relative">
      <div className="pt-24 pb-16">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-8">

          {/* Page Header */}
          <section className="pt-12 sm:pt-16 pb-10">
            <div className="max-w-[1100px] mx-auto">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-text-primary leading-tight mb-5">
                Systems, Explained
              </h1>
              <p className="text-xl sm:text-2xl font-light text-text-primary leading-tight mb-5">
                Common system designs, explained through real-world constraints — not buzzwords.
              </p>
              <div className="space-y-2.5 text-text-secondary leading-relaxed max-w-3xl mb-4">
                <p className="text-base">
                  Tools change.
                </p>
                <p className="text-base">
                  Constraints don't.
                </p>
                <p className="text-base">
                  These are the kinds of systems I design — and how I think about them.
                </p>
              </div>
              <p className="text-sm text-text-muted/80 leading-relaxed max-w-3xl">
                Each system below shows how responsibility, data, and failure move through the architecture.
              </p>
            </div>
          </section>

          {/* Divider */}
          <div className="relative py-6">
            <div className="absolute top-0 left-0 right-0 h-px bg-border-subtle opacity-60"></div>
          </div>

          {/* System Selector */}
          <section className="py-5">
            <div className="max-w-[1100px] mx-auto">
              <div className="flex flex-wrap gap-3 mb-3 justify-center sm:justify-start">
                {(['content', 'webapp', 'traffic', 'app'] as SystemType[]).map((system) => (
                  <button
                    key={system}
                    onClick={() => handleSystemChange(system)}
                    className={`px-4 py-2 rounded-sm border transition-all duration-200 text-sm font-mono ${
                      selectedSystem === system
                        ? 'bg-accent-primary/15 border-accent-primary/60 text-accent-primary shadow-sm'
                        : 'bg-bg-elevated border-border-subtle text-text-secondary hover:border-accent-primary/30 hover:text-text-primary'
                    }`}
                  >
                    {systemData[system].title}
                  </button>
                ))}
              </div>
              <p className="text-xs text-text-muted font-light text-center sm:text-left">
                Hover components to see connections. Click for details.
              </p>
            </div>
          </section>

          {/* System Diagram */}
          <section className="py-6 sm:py-8">
            <div className="max-w-[1100px] mx-auto">
              {/* Framing line */}
              <div className="mb-6 max-w-3xl mx-auto">
                <p className="text-sm text-text-muted leading-relaxed">
                  {currentSystem.narrative.optimizes}
                </p>
              </div>

              <div className="bg-bg-elevated border border-border-subtle rounded-sm p-4 sm:p-6 md:p-8 mb-6">
                <SystemDiagram
                  system={currentSystem}
                  onComponentClick={handleComponentClick}
                  failureScenario={currentFailureScenario}
                />
              </div>

              {/* Failure Scenario Cycle Button */}
              <div className="flex flex-col items-center gap-4 mb-6">
                <button
                  onClick={cycleFailureScenario}
                  className={`flex items-center gap-3 px-5 py-3 rounded-sm border transition-all duration-300 ${
                    failureScenarioIndex !== null
                      ? 'bg-red-500/15 border-red-500/60 text-red-400 shadow-sm shadow-red-500/20'
                      : 'bg-bg-elevated border-border-subtle text-text-secondary hover:border-red-500/30'
                  }`}
                >
                  <div className={`w-3 h-3 rounded-full ${
                    failureScenarioIndex !== null ? 'bg-red-500 animate-pulse' : 'bg-green-500'
                  }`}></div>
                  <span className="text-sm font-mono">
                    {failureScenarioIndex !== null
                      ? `Failure ${failureScenarioIndex + 1}/${currentSystem.failureScenarios.length}: ${currentFailureScenario?.name}`
                      : 'Simulate Failure (Click to Cycle)'}
                  </span>
                </button>

                {/* Failure Details */}
                {currentFailureScenario && (
                  <div className="w-full max-w-3xl bg-red-500/10 border border-red-500/30 rounded-sm p-5">
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-sm font-mono text-red-400 uppercase tracking-wider mb-1">What Happens</h4>
                        <p className="text-sm text-red-200 leading-relaxed">{currentFailureScenario.description}</p>
                      </div>
                      <div className="border-t border-red-500/20 pt-3">
                        <h4 className="text-sm font-mono text-red-400 uppercase tracking-wider mb-1">Impact</h4>
                        <p className="text-sm text-red-200/90 leading-relaxed">{currentFailureScenario.impact}</p>
                      </div>
                      <div className="border-t border-red-500/20 pt-3">
                        <h4 className="text-sm font-mono text-amber-400 uppercase tracking-wider mb-1">How to Prevent</h4>
                        <p className="text-sm text-amber-200/90 leading-relaxed">{currentFailureScenario.mitigation}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Supporting Narrative */}
            <div className="max-w-[1100px] mx-auto pt-4">
              <div className="space-y-4 text-text-secondary leading-relaxed max-w-3xl mx-auto">
                <p className="text-base">
                  {currentSystem.narrative.breaksDown}
                </p>
                <p className="text-sm text-text-muted italic pt-2">
                  {currentSystem.narrative.takeaway}
                </p>
              </div>
            </div>
          </section>

          {/* Footer Quote */}
          <div className="pt-8">
            <div className="max-w-[1100px] mx-auto">
              <div className="bg-bg-elevated border border-border-subtle rounded-sm p-8 sm:p-10 text-center">
                <p className="text-base sm:text-lg text-accent-primary/80 leading-relaxed font-light italic max-w-2xl mx-auto">
                  Systems don't need to be loud to be effective.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="relative pt-8">
            <Footer maxWidth="max-w-[1100px]" />
          </div>

        </div>
      </div>

      {/* Sidebar for component details */}
      <SystemSidebar
        component={currentComponent}
        isOpen={!!selectedComponent}
        onClose={() => setSelectedComponent(null)}
      />
    </div>
  )
}
